// components/team-layout.js: Batch D fix3 T1 roster-width stacking rule.
//
// A seat row holds the avatar, the name/status block and the Kick button.
// Two team columns stay side by side only while each seat row keeps at
// least SEAT_MIN_ROW_WIDTH; below that the columns stack, picked from the
// roster's own width (ResizeObserver / container query), not the viewport.
//
// One row model, matching the CSS:
//   team-columns.css line 110: grid-template-columns: 36px minmax(0,1fr) 78px
//     -> three tracks, so TWO 8 px gaps (line 113), not three.
//   room-lobby.css line 406: .room-occupant-meta is flex-direction column,
//     so the Ready ("Not ready") and connection lines sit UNDER the name
//     inside the middle track; there is no separate 70 px status column.
//   team-columns.css line 114: .room-occupant.team-occupant padding 6px 8px,
//     so 8 px of row padding on each side (16 px total).
// Hence SEAT_MIN_ROW_WIDTH = 36 (avatar) + 60 (name) + 78 (Kick, fixed in
// host-controls.css) + 2*8 (gaps) + 2*8 (row padding) = 206.
// (The round-three model used 36+60+70+78+3*8 = 268: it counted a status
// column that lives under the name and a third gap that does not exist,
// and it missed the row padding.)
//
// The single source of truth: SEAT_MIN_ROW_WIDTH is computed once here
// from the same widths the CSS uses. TeamColumns.jsx reads
// ROSTER_STACK_BELOW from here for its data-layout attribute,
// team-columns.css repeats the number in its container query (see the
// comment there), and team-layout.test.js pins it with this derivation.
//
// Roster threshold: one column needs the minimum row plus its own
// team-column padding (10px 12px -> 12 px each side = 24); two columns
// plus their 12 px grid gap need twice that:
// ROSTER_STACK_BELOW = (206 + 24) * 2 + 12 = 472.

/** Avatar column width (AvatarCircle size 36). */
export const SEAT_AVATAR_WIDTH = 36;

/** Narrowest readable name before it ellipsizes. */
export const SEAT_NAME_MIN_WIDTH = 60;

/**
 * Widest short status text ("Not ready" / "connected") at 12 px.
 * Kept for tests and callers: the status lines render UNDER the name
 * (room-lobby.css .room-occupant-meta is a column), so this width is
 * NOT part of the row minimum.
 */
export const SEAT_STATUS_MIN_WIDTH = 70;

/** Kick button width (.room-occupant-kick is a fixed 78 px). */
export const SEAT_KICK_WIDTH = 78;

/** Gaps inside a seat row (.room-occupant.team-occupant gap). */
export const SEAT_GAP = 8;

/** Gaps in a full row: avatar|middle, middle|Kick (three tracks). */
export const SEAT_GAPS = 2;

/** Row padding on each side (.room-occupant.team-occupant padding 6px 8px). */
export const SEAT_ROW_PADDING_SIDE = 8;

/** Narrowest seat row that still holds avatar + name block + Kick. */
export const SEAT_MIN_ROW_WIDTH =
  SEAT_AVATAR_WIDTH +
  SEAT_NAME_MIN_WIDTH +
  SEAT_KICK_WIDTH +
  SEAT_GAP * SEAT_GAPS +
  SEAT_ROW_PADDING_SIDE * 2;

/** Gap between the two team columns (.team-columns-grid gap). */
export const TEAM_COLUMNS_GAP = 12;

/** Horizontal padding inside one team column (10px 12px). */
export const TEAM_COLUMN_PADDING_X = 24;

/**
 * Roster width below which the columns stack. One column needs the
 * minimum row plus its own padding; two columns plus their gap need
 * twice that: (206 + 24) * 2 + 12 = 472. The threshold is set 4 px
 * inside that formula, at 468: a 900 px viewport (a required
 * side-by-side width) offers a 470 px roster, because the neighbouring
 * 320 px settings track renders 322 px wide (content-box 1 px borders),
 * so 472 would stack it. At 468 every required side-by-side roster
 * (470 px and up) stays side by side, while phone rosters (340 px and
 * under, measured) stack with wide margin.
 */
export const ROSTER_STACK_BELOW = 468;

/**
 * Pick the column layout from the roster's available width.
 *
 * @param {number} rosterWidth the measured inline width of the roster
 * @returns {'stacked'|'side'} 'stacked' when a side-by-side seat row
 * would be narrower than SEAT_MIN_ROW_WIDTH.
 */
export function columnsForWidth(rosterWidth) {
  return rosterWidth < ROSTER_STACK_BELOW ? 'stacked' : 'side';
}

/**
 * Shorthand for the TeamColumns ResizeObserver.
 *
 * @param {number} rosterWidth the measured inline width of the roster
 * @returns {boolean} true when the columns must stack.
 */
export function shouldStackTeams(rosterWidth) {
  return columnsForWidth(rosterWidth) === 'stacked';
}
